import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { thinScrollbarStyles } from 'shared/design-system/settings/variables/Scrollbar';

const ResizeHandle = styled.div<{ $isDragging?: boolean }>`
  width: 8px;
  background: ${props =>
    props.$isDragging ? COLORS.ctaPrimary : COLORS.grayLightest};
  cursor: col-resize;
  transition: background-color 0.2s;
  position: relative;
  user-select: none;
  z-index: 2;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 3px;
    width: 2px;
    height: 100%;
    background: ${props =>
      props.$isDragging ? COLORS.ctaPrimaryBackground : COLORS.grayLight};
    opacity: ${props => (props.$isDragging ? '0.8' : '0.5')};
  }

  &:hover,
  &:active {
    background: ${props =>
      props.$isDragging ? COLORS.ctaPrimary : COLORS.ctaPrimaryBackground};
    &::after {
      opacity: 0.8;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -4px;
    right: -4px;
    bottom: 0;
    z-index: 1;
  }
`;

const ResizableContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const LeftPanel = styled.div<{ width: number }>`
  width: ${props => props.width}%;
  height: 100%;
  overflow: auto;
  position: relative;
  flex-shrink: 0;
  .left-panel-content {
    height: 100%;
    overflow: auto;
    ${thinScrollbarStyles}
  }
`;

const RightPanel = styled.div`
  flex: 1;
  height: 100%;
  overflow: auto;
  position: relative;
  will-change: width;
  transform: translateZ(0);
`;

interface ResizableSplitViewProps {
  left: React.ReactNode;
  right: React.ReactNode;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  onReset?: () => void;
}

export const ResizableSplitView: React.FC<ResizableSplitViewProps> = ({
  left,
  right,
  defaultWidth = 50,
  minWidth = 20,
  maxWidth = 80,
  onReset,
}) => {
  const [width, setWidth] = useState(defaultWidth);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const lastClickTime = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();

    // Handle double click
    const now = Date.now();
    if (now - lastClickTime.current < 300) {
      // 300ms threshold for double click
      setWidth(defaultWidth);
      if (onReset) onReset();
      return;
    }
    lastClickTime.current = now;

    // Handle drag start
    startX.current = e.clientX;
    startWidth.current = width;
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const deltaX = e.clientX - startX.current;
    const newWidth = Math.min(
      maxWidth,
      Math.max(minWidth, startWidth.current + (deltaX / containerWidth) * 100),
    );

    setWidth(newWidth);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <ResizableContainer ref={containerRef}>
      <LeftPanel width={width}>
        <div className="left-panel-content">{left}</div>
      </LeftPanel>

      <ResizeHandle
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        title="Drag to resize or double-click to reset"
        $isDragging={isDragging}
      />

      <RightPanel>{right}</RightPanel>

      {isDragging && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            cursor: 'col-resize',
          }}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      )}
    </ResizableContainer>
  );
};
